<script>
import date from 'locutus/php/datetime/date'
import {
  fetchPolicyBeneficiaryConditions,
  fetchPolicyBeneficiaryCondition,
  createPolicyBeneficiaryCondition,
  updatePolicyBeneficiaryCondition,
  deletePolicyBeneficiaryCondition,
} from '@apis/admin/affiliate'
import { fetchGroups } from '@apis/admin/member'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  props: {
    beneficiary_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      conditions: [],
      groups: [],
      target_condition_seq: 0,
      show_condition_info: false,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    beneficiary_seq() {
      this.init()
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([this.loadConditions(), this.loadGroups()]).catch((e) =>
        this.$toasted.error(e.message)
      )
    },
    async loadConditions() {
      const { data } = await fetchPolicyBeneficiaryConditions(
        null,
        this.beneficiary_seq
      )

      this.conditions = data
    },
    async loadGroups() {
      const { data } = await fetchGroups()

      this.groups = data
    },
    handleAddCondition() {
      this.conditions.push({
        list_order: 0,
        beneficiary_seq: this.beneficiary_seq,
        condition_type: undefined,
        group_seq: undefined,
      })
    },
    async handleDeleteCondition(idx) {
      this.loading = true
      try {
        if (this.conditions[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deletePolicyBeneficiaryCondition(
            null,
            this.beneficiary_seq,
            this.conditions[idx].seq
          )
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.conditions.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitCondition(idx) {
      this.loading = true
      const params = this.conditions[idx]
      ;(params.seq
        ? updatePolicyBeneficiaryCondition
        : createPolicyBeneficiaryCondition)(params)
        .then(({ seq }) =>
          fetchPolicyBeneficiaryCondition(null, this.beneficiary_seq, seq)
        )
        .then(({ condition_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.conditions, idx, { ...condition_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <div>
    <table
      v-if="beneficiary_seq"
      cellspacing="0"
      cellpadding="0"
      :class="$style.colTable"
    >
      <thead>
        <tr>
          <th scope="col">{{
            $t('affiliate_policy_beneficiary_condition.order')
          }}</th>
          <th scope="col">{{
            $t('affiliate_policy_beneficiary_condition.condition_satisfied')
          }}</th>
          <th scope="col">
            <div :class="$style.tRight">
              <a
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleAddCondition"
                ><span>{{ $t('admin.add') }}</span></a
              >
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(condition, idx) in conditions"
          :key="condition.seq ? `${condition.seq}` : `create-${idx}`"
        >
          <td :class="[$style.tCenter, $style.nowrap]">
            <input
              v-model="condition.list_order"
              type="text"
              :class="[$style.inputTypeText, $style.w40, $style.tRight]"
            />
          </td>
          <td :class="$style.wide">
            <select
              v-model="condition.condition_type"
              :disabled="condition.seq"
            >
              <option :value="undefined">{{
                $t('admin.restriction_select')
              }}</option>
              <option
                v-for="[type, type_title] in constants.affiliate
                  .policy_beneficiary_condition.condition_type"
                :key="type"
                :value="type"
              >
                {{ type_title }}
              </option>
            </select>
            <template v-if="condition.condition_type === 'MEMBER_GROUP'">
              이
              <select
                v-if="condition.condition_type === 'MEMBER_GROUP'"
                v-model="condition.group_seq"
              >
                <option :value="undefined">{{
                  $t('admin.restriction_select')
                }}</option>
                <option
                  v-for="{ seq, title } in groups"
                  :key="seq"
                  :value="seq"
                >
                  {{ title }}
                </option>
              </select>
            </template>
            이어야 함
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <a
              v-if="condition.seq"
              href="#"
              :class="[$style.button]"
              @click="handleSubmitCondition(idx)"
              ><span>{{ $t('admin.modify') }}</span></a
            >
            <a
              v-else
              href="#"
              :class="[$style.black, $style.button]"
              @click="handleSubmitCondition(idx)"
              ><span>{{ $t('admin.enroll') }}</span></a
            >
            {{ ' ' }}
            <a
              href="#"
              :class="[$style.red, $style.button]"
              @click="handleDeleteCondition(idx)"
              ><span>{{ $t('admin.delete') }}</span></a
            >
          </td>
        </tr>
        <tr v-if="!conditions.length">
          <td :class="$style.tCenter" colspan="3">{{
            $t('affiliate_policy_beneficiary_condition.msg_allowance_policy_no')
          }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
.adminRightExtra p {
  margin: 10px 0;
}
</style>
